import { t } from 'i18next';
import { chunk, isEmpty, throttle } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocationPostsQuery } from '../../../components/api/queries/useLocationsQuery';
import useUserQuery from '../../../components/api/queries/useUserQuery';
import AddUserProfileV1BottomSheet from '../../../components/bottomSheet/AddUserProfileV1BottomSheet';
import InfiniteScroll from '../../../components/InfiniteScroll';
import { IPostsResponseBody } from '../../../types/response/IPostsResponseBody';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';

const PASTEL_COLORS = ['#FFFACD', '#E0FFFF', '#FFE4E1', '#F0FFF0', '#E6E6FA', '#FFF0F5'];

const getRandomColor = () => PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];

interface PostItWrapperProps {
  children: React.ReactNode;
  backgroundColor?: string;
  style?: React.CSSProperties;
  onClick: () => void;
}

const PostItWrapper: React.FC<PostItWrapperProps> = ({
  children,
  backgroundColor,
  style,
  onClick
}) => (
  <div
    className="w-[180px] min-w-[160px] max-w-[180px] min-h-[160px] content-center rounded-[5%] p-[5.5%] shadow-[0px_2px_10px_rgba(0,0,0,0.1)] transition-all duration-200 ease-in-out"
    style={{
      backgroundColor,
      ...style
    }}
    onClick={onClick}
  >
    {children}
  </div>
);

interface PostItProps {
  post: IPostsResponseBody;
  index: number;
}

const PostIt: React.FC<PostItProps> = ({ post, index }) => {
  const rotation = Math.floor(Math.random() * 17);
  const scale = 0.95;
  const overlap = Math.random() < 0.3;
  const translateX = index % 2 === 0 ? -20 : 20;
  const translateY = overlap ? Math.random() * 30 - 10 : 0;
  const zIndex = overlap ? 20 : 1;

  return (
    <PostItWrapper
      backgroundColor={getRandomColor()}
      style={{
        transform: `rotate(${
          index % 2 === 0 ? rotation : -rotation
        }deg) scale(${scale}) translate(${translateX}px, ${translateY}px)`,
        zIndex
      }}
      onClick={() => openNewTab(`/posts/${post.postId}`)}
    >
      <h4>{post.title}</h4>
      <p className="text-[14px] font-normal text-black/60">{post.content}</p>
    </PostItWrapper>
  );
};

interface PostItGridProps {
  posts: IPostsResponseBody[];
  startIndex?: number;
}

const PostItGrid: React.FC<PostItGridProps> = ({ posts, startIndex = 1 }) => {
  if (isEmpty(posts)) return null;

  return (
    <div className="flex flex-row justify-center mb-[12px] gap-[12px]">
      {posts.map((post, idx) => (
        <PostIt key={post.postId} post={post} index={startIndex + idx} />
      ))}
    </div>
  );
};

interface AddPostButtonProps {
  onClick: () => void;
}

const AddPostButton: React.FC<AddPostButtonProps> = ({ onClick }) => (
  <PostItWrapper
    backgroundColor={PASTEL_COLORS[0]}
    onClick={onClick}
    style={{
      transform: 'rotate(-5deg) scale(0.9)',
      zIndex: 1000
    }}
  >
    <div className="flex flex-col items-center gap-3 mt-4 mb-6">
      <div className="flex items-center justify-center w-12 h-12 bg-[rgba(0,0,0,0.04)] rounded-full cursor-pointer">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
        >
          <title>새로 작성하기</title>
          <path
            d="M12 5V19M5 12H19"
            stroke="#222222"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <span className="text-[14px] font-medium text-[#444444]">새로 작성하기</span>
    </div>
  </PostItWrapper>
);

interface EventPostsProps {
  locationId: string;
  eventId?: string;
}

const PostItEventPosts: React.FC<EventPostsProps> = ({ locationId, eventId }) => {
  const queryClient = useQueryClient();
  const [isOpenAddProfileBottomSheet, setIsOpenAddProfileBottomSheet] = useState(false);
  const user = useUserQuery();

  const {
    data: PostsData,
    isFetching,
    fetchNextPage,
    hasNextPage
  } = useLocationPostsQuery({
    locationId: locationId,
    eventId: eventId,
    perPage: 5
  });

  const posts = useMemo(() => {
    if (!PostsData) return [];
    return PostsData.pages?.flatMap((page) => page.posts);
  }, [PostsData]);

  const chunkedPosts = useMemo(() => {
    if (isEmpty(posts)) return [];
    return chunk(posts.slice(1), 2);
  }, [posts]);

  useEffect(() => {
    const handlePostCreationComplete = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || isEmpty(event.data?.type)) return;

      if (event.data.type === 'post-creation-complete' && event.data.params?.post) {
        queryClient.invalidateQueries('LocationPosts');
      }
    };

    const throttledReceiveMessage = throttle(handlePostCreationComplete, 1000);
    window.addEventListener('message', throttledReceiveMessage);

    return () => {
      window.removeEventListener('message', throttledReceiveMessage);
    };
  }, [queryClient]);

  const navigateToAddPost = () => {
    if (user?.baseUserProfile) {
      openNewTab(`/posts/add/?locationId=${locationId}&eventId=${eventId}&category=FreeTalk`);
    } else {
      setIsOpenAddProfileBottomSheet(true);
    }
  };

  return (
    <>
      <div className="py-[10px] font-bold text-main-light-2 dark:text-main-dark-2">
        {t('OfflineEventDetail.postItTitle')}
      </div>

      <div className="flex flex-row justify-center mt-4 mb-[12px] gap-[12px]">
        <AddPostButton onClick={() => navigateToAddPost()} />
        {!isEmpty(posts) && <PostIt post={posts[0]} index={0} />}
      </div>

      <InfiniteScroll
        items={chunkedPosts}
        loadMore={fetchNextPage}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        renderItem={(chunkedPosts: IPostsResponseBody[]) => <PostItGrid posts={chunkedPosts} />}
      />

      {isOpenAddProfileBottomSheet && (
        <AddUserProfileV1BottomSheet
          title={t('createFeedUserProfilePage.title')}
          userProfile={undefined}
          isShowProfileBottomSheet={isOpenAddProfileBottomSheet}
          setIsShowProfileBottomSheet={setIsOpenAddProfileBottomSheet}
          category={'base'}
          onCompleted={() => setIsOpenAddProfileBottomSheet(false)}
        />
      )}
    </>
  );
};

export default PostItEventPosts;
